import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { MessageCircle, Send, Edit2, Trash2, X, Check, Pin, PinOff, Megaphone } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';

const InternalNotes = forwardRef(({ repairId, onRefresh }, ref) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [reportingCommentId, setReportingCommentId] = useState(null);
  // Kept separate from `error` so a failed report doesn't replace the whole notes list
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState('');

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!repairId) {
        setComments([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(`/api/repairs/${repairId}/comments`);
      
      // Sort comments: pinned first, then by timestamp (newest first)
      const sortedComments = (response.data || [])
        .sort((a, b) => {
          // Pinned comments come first
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          // Within the same pin status, newest first
          return new Date(b.created_at) - new Date(a.created_at);
        });
      
      setComments(sortedComments);
      setError('');
    } catch (err) {
      console.error('Error fetching internal notes:', err);
      setError('Failed to load internal notes');
    } finally {
      setLoading(false);
    }
  }, [repairId]);

  useEffect(() => {
    if (repairId) {
      fetchComments();
    }
  }, [repairId, fetchComments]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchComments
  }), [fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !user || !repairId) {
      return;
    }

    try {
      setSending(true);
      await axios.post(`/api/repairs/${repairId}/comments`, {
        comment_text: newComment.trim(),
        created_by: user.id
      });
      
      setNewComment('');
      await fetchComments(); // Refresh to show the new comment
      setError('');
      
      // Call onRefresh callback if provided (to refresh activity in parent)
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error adding internal note:', err);
      setError('Failed to add internal note');
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditingText(comment.comment_text);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editingText.trim() || !user || !repairId) {
      return;
    }

    try {
      await axios.put(`/api/repairs/${repairId}/comments/${commentId}`, {
        comment_text: editingText.trim(),
        user_id: user.id
      });
      
      setEditingCommentId(null);
      setEditingText('');
      await fetchComments(); // Refresh to show the updated comment
      setError('');
      
      // Call onRefresh callback if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating internal note:', err);
      setError(err.response?.data?.error || 'Failed to update internal note');
    }
  };

  const handleDelete = async (commentId) => {
    if (!user || !repairId) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this internal note?')) {
      return;
    }

    try {
      setDeletingCommentId(commentId);
      await axios.delete(`/api/repairs/${repairId}/comments/${commentId}`, {
        params: { user_id: user.id }
      });
      
      await fetchComments(); // Refresh to remove the deleted comment
      setError('');
      
      // Call onRefresh callback if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error deleting internal note:', err);
      setError(err.response?.data?.error || 'Failed to delete internal note');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleTogglePin = async (commentId) => {
    if (!repairId) return;

    try {
      await axios.patch(`/api/repairs/${repairId}/comments/${commentId}/pin`);
      await fetchComments();
      setError('');

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error toggling pin on internal note:', err);
      setError(err.response?.data?.error || 'Failed to update pin status');
    }
  };

  const handleReportToTeam = async (comment) => {
    if (!repairId) return;

    const alreadyReported = !!comment.reported_at;
    const confirmMessage = alreadyReported
      ? 'This note was already reported. Send it to the team again?'
      : 'Send this note to the team by email?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setReportingCommentId(comment.id);
      setReportError('');
      setReportSuccess('');

      const response = await axios.post(`/api/repairs/${repairId}/comments/${comment.id}/report`);

      await fetchComments();
      setReportSuccess(response.data?.message || 'Note reported to the team');

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error reporting internal note to team:', err);
      setReportError(err.response?.data?.error || 'Failed to report note to the team');
    } finally {
      setReportingCommentId(null);
    }
  };

  if (loading) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading internal notes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

    return (
    <div id="internal-notes">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>

      {/* Report feedback - inline so the notes list stays visible */}
      {reportError && (
        <div className="mb-3 flex items-start justify-between bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <span className="text-sm text-red-700">{reportError}</span>
          <button
            onClick={() => setReportError('')}
            className="ml-2 text-red-400 hover:text-red-600"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {reportSuccess && (
        <div className="mb-3 flex items-start justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
          <span className="text-sm text-green-700">{reportSuccess}</span>
          <button
            onClick={() => setReportSuccess('')}
            className="ml-2 text-green-400 hover:text-green-600"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Add Comment Form */}
      {repairId && (
        <form onSubmit={handleSubmitComment} className="mb-4">
          <div className="relative">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add an internal note..."
              rows={1}
              className="w-full px-3 py-2 pr-12 border-2 border-gray-200 rounded-lg shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                       transition-all duration-200 resize-none text-sm"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitComment(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || sending}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 
                       rounded-full text-white bg-naviter-blue hover:bg-naviter-blue-dark 
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue 
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-naviter-blue
                       transition-all duration-200 shadow-sm hover:shadow-md p-0"
              title="Send note (Enter)"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                <Send className="h-4 w-4" style={{ marginLeft: '-1px', marginTop: '1px' }} />
              )}
            </button>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center">
              <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              Internal only - not visible to customer
            </span>
            <span className="text-gray-400">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Enter</kbd> to send
            </span>
          </div>
        </form>
      )}
      
      {comments.length === 0 ? (
        <div className="text-center py-8">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No internal notes yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add internal notes to track repair progress and communicate with your team.
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Vertical timeline line - centered on the dots */}
          <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
          
          {/* Comments list */}
          <div className="space-y-3">
            {comments.map((comment, index) => {
              // Compare as strings to handle type mismatches (PostgreSQL BIGINT returns as string)
              const isOwnComment = user && String(comment.created_by) === String(user.id);
              const isEditing = editingCommentId === comment.id;
              const isDeleting = deletingCommentId === comment.id;
              const isPinned = comment.is_pinned;
              const isReporting = reportingCommentId === comment.id;
              const isReported = !!comment.reported_at;
              
              return (
                <div key={comment.id || index} className="relative flex items-start">
                  {/* Status dot with icon */}
                  <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white ${isPinned ? 'bg-amber-500' : 'bg-blue-400'}`}>
                    {isPinned ? (
                      <Pin className="h-3 w-3 text-white" />
                    ) : (
                      <MessageCircle className="h-3 w-3 text-white" />
                    )}
                  </div>
                  
                  {/* Comment content */}
                  <div className="ml-3 min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-900">
                        <span className="font-medium text-gray-500">
                          {formatDateTime(comment.created_at)}
                        </span>
                        <span className="mx-1 text-gray-400">•</span>
                        <span className="font-medium text-gray-900">
                          {comment.created_by_name || 'Unknown'}
                        </span>
                        {isPinned && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Pinned
                          </span>
                        )}
                        {isReported && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Reported
                          </span>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      {!isEditing && (
                        <div className="flex items-center space-x-1">
                          {/* Report to the team - visible for all users, on any note */}
                          <button
                            onClick={() => handleReportToTeam(comment)}
                            className={`p-0.5 transition-colors ${isReported ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-naviter-blue'}`}
                            title={
                              isReported
                                ? `Reported to team on ${formatDateTime(comment.reported_at)}${comment.reported_by_name ? ` by ${comment.reported_by_name}` : ''} - click to send again`
                                : 'Report to the team'
                            }
                            disabled={isReporting}
                          >
                            {isReporting ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-naviter-blue border-t-transparent"></div>
                            ) : (
                              <Megaphone className="h-3 w-3" />
                            )}
                          </button>

                          {/* Pin/Unpin button - visible for all users */}
                          <button
                            onClick={() => handleTogglePin(comment.id)}
                            className={`p-0.5 transition-colors ${isPinned ? 'text-amber-500 hover:text-amber-700' : 'text-gray-400 hover:text-amber-500'}`}
                            title={isPinned ? 'Unpin note' : 'Pin note'}
                          >
                            {isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                          </button>
                          
                          {/* Edit/Delete buttons - only show for own comments */}
                          {isOwnComment && (
                            <>
                              <button
                                onClick={() => handleStartEdit(comment)}
                                className="p-0.5 text-gray-400 hover:text-naviter-blue transition-colors"
                                title="Edit note"
                                disabled={isDeleting}
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(comment.id)}
                                className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete note"
                                disabled={isDeleting}
                              >
                                {isDeleting ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-600 border-t-transparent"></div>
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Comment text - editable when editing */}
                    {isEditing ? (
                      <div className="mt-1">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          rows={3}
                          className="w-full px-2 py-1 border-2 border-naviter-blue/30 rounded-md shadow-sm 
                                   focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent 
                                   resize-none text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 
                                     rounded-md transition-colors flex items-center space-x-1"
                          >
                            <X className="h-3 w-3" />
                            <span>Cancel</span>
                          </button>
                          <button
                            onClick={() => handleSaveEdit(comment.id)}
                            disabled={!editingText.trim()}
                            className="px-2 py-1 text-xs text-white bg-naviter-blue hover:bg-naviter-blue-dark 
                                     rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue
                                     flex items-center space-x-1"
                          >
                            <Check className="h-3 w-3" />
                            <span>Save</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`mt-1 p-2 rounded-md border-l-2 ${isPinned ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-blue-200'}`}>
                        <p className={`text-sm whitespace-pre-wrap leading-snug ${isPinned ? 'text-amber-900' : 'text-gray-700'}`}>
                          {comment.comment_text}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export default InternalNotes;

